// Top-right notification bell. Polls the backend every 30s for new
// rows, renders a badge with the unread count, and opens a dropdown
// with the most recent items. Clicking a row marks it read and
// navigates to its `link` (e.g. /clients/<id>) via the existing
// SPA setView contract — we dispatch a `deskte:nav` CustomEvent
// the shell already listens for.
//
// The bell intentionally excludes the dashboard's existing alerts
// (renewals, missing docs, birthdays). Those live in derived views
// and we don't want this bell to become a noisy duplicate. Bell rows
// come from explicit `createNotification(...)` calls in write paths
// (inbound email, recurring invoice, portal doc upload, lead form).

import { useEffect, useRef, useState } from 'react';
import { intervaloVisible } from '../relojVisible.js';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { haceCuanto, horaExacta, cuerpoDelAviso } from '../horaDelAviso.js';
import { apiFetch } from '../AuthGate.jsx';
import { sonarNotificacion, sonarCita, esCitaReservada, sonidoActivado, ponerSonido } from '../sonidos.js';

const POLL_MS = 30000;

export function NotificationBell({ lang, onNavigate }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // El interruptor del sonido de los avisos. Vive aqui, en el panel de la
  // campana, porque es donde se busca cuando molesta. Vale para las DOS
  // campanas: apagarlo calla tambien los anuncios (Lissette 2026-09-02).
  const [conSonido, setConSonido] = useState(() => sonidoActivado());
  const popoverRef = useRef(null);
  // Los avisos que ya habia visto esta pestaña, para saber cuales son NUEVOS.
  // Arranca en null a proposito: la primera respuesta solo los apunta y no
  // suena. Si sonara, la campana pitaria en cada recarga de la pagina por
  // avisos que ya estaban ahi desde ayer (Lissette 2026-09-02, queja 10).
  //
  // Se comparan los AVISOS, no el numero de sin leer. Ese numero sale de los
  // 50 mas recientes, asi que quien tiene 50 o mas sin leer lo ve clavado en
  // 50: entra uno nuevo, sale uno viejo por abajo y el contador no sube. Por
  // eso a Wendy no le sonaba una cita reservada (2026-10-27).
  const yaVistos = useRef(null);

  const refresh = async () => {
    try {
      const r = await apiFetch('/api/v1/notifications?limit=50');
      if (!r.ok) return;
      const json = await r.json();
      const lista = json.data || [];
      setItems(lista);
      setUnread(json.unreadCount || 0);
      // Solo por un aviso sin leer que no estaba antes. Marcar leido o borrar
      // no suena.
      if (yaVistos.current !== null) {
        const nuevos = lista.filter((n) => !n.read && !yaVistos.current.has(n.id));
        if (nuevos.some((n) => esCitaReservada(n.type))) sonarCita();
        else if (nuevos.length > 0) sonarNotificacion();
      } else {
        yaVistos.current = new Set();
      }
      lista.forEach((n) => yaVistos.current.add(n.id));
    } catch {
      // Network blip — try again on next poll.
    }
  };

  useEffect(() => {
    refresh();
    // Solo mientras la pestaña se mira, con un refresco en el acto al volver:
    // la campana tiene que estar al dia cuando la persona MIRA, no cuando esta
    // en otra ventana. Ver crm-app/relojVisible.js. (2026-09-02.)
    const pararReloj = intervaloVisible(refresh, POLL_MS);
    // Live nudge from the SSE channel (notification.created → shell dispatches
    // deskte:notification): refetch immediately so the badge appears the moment
    // a client views/accepts/signs, keeping the 30s poll as the fallback.
    const onLive = () => refresh();
    window.addEventListener('deskte:notification', onLive);
    return () => {
      pararReloj();
      window.removeEventListener('deskte:notification', onLive);
    };
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    setUnread((n) => Math.max(0, n - 1));
    try {
      await apiFetch(`/api/v1/notifications/${id}/read`, { method: 'POST' });
    } catch {
      // Ignore — next refresh reconciles.
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    setUnread(0);
    try {
      await apiFetch('/api/v1/notifications/mark-all-read', { method: 'POST' });
    } catch {
      // Ignore.
    }
  };

  // Fallback target when a notification has no explicit link, so EVERY row is
  // clickable and lands somewhere useful (Lissette 2026-06-19 #15: "las alertas
  // deben ser clickeables y llevar al item").
  const typeFallbackLink = (type) => {
    const t = String(type || '');
    if (t.startsWith('lead')) return '/leads';
    if (t.startsWith('quote')) return '/quotes';
    if (t.startsWith('invoice') || t.startsWith('recurring')) return '/invoices';
    if (t.startsWith('application')) return '/applications';
    if (t.startsWith('signature')) return '/signatures';
    if (t.startsWith('booking') || t.startsWith('appointment') || t.startsWith('calendar')) return '/calendar';
    if (t.startsWith('doc')) return '/clients';
    if (t.startsWith('email')) return '/my_email';
    if (t.startsWith('task')) return '/tasks';
    if (t.startsWith('ticket')) return '/tickets';
    return '/dashboard';
  };

  const onItemClick = async (item) => {
    if (!item.read) await markRead(item.id);
    const link = item.link || typeFallbackLink(item.type);
    if (link && typeof onNavigate === 'function') onNavigate(link);
    setOpen(false);
  };


  return (
    <div ref={popoverRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="dkt-hpill"
        title={lang === 'es' ? 'Notificaciones' : 'Notifications'}
        style={{ position: 'relative' }}
      >
        <Bell size={14} style={{ color: unread > 0 ? 'var(--c-amber,#ffb700)' : 'var(--c-text2,#7aa3c0)' }} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            minWidth: 14, height: 14, padding: '0 3px',
            borderRadius: 100, background: 'var(--c-red,#ef4444)',
            color: '#fff', fontSize: '.6rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
          width: 360, maxWidth: 'calc(100vw - 32px)',
          background: 'var(--c-surface,#0d1c2e)',
          border: '1px solid var(--c-border,#1a3550)',
          borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.5)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '.6rem .85rem', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '1px solid var(--c-border,#1a3550)',
          }}>
            <div style={{ fontSize: '.8rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
              🔔 {lang === 'es' ? 'Notificaciones' : 'Notifications'}
              {unread > 0 && (
                <span style={{ marginLeft: 6, fontSize: '.65rem', color: 'var(--c-amber,#ffb700)', fontWeight: 700 }}>
                  ({unread})
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Sonido si/no. Al ENCENDERLO suena una vez, para que se oiga como
                quedo y a que volumen — y de paso desbloquea el audio del
                navegador, que exige un gesto de la persona antes de dejar
                sonar nada. */}
            <button
              type="button"
              title={lang === 'es'
                ? (conSonido ? 'Sonido activado — tócalo para silenciar' : 'Sonido silenciado — tócalo para activarlo')
                : (conSonido ? 'Sound on — click to mute' : 'Sound muted — click to turn on')}
              onClick={() => {
                const siguiente = !conSonido;
                setConSonido(siguiente);
                ponerSonido(siguiente);
                if (siguiente) sonarNotificacion();
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                display: 'inline-flex', alignItems: 'center',
                color: conSonido ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)',
              }}
            >
              {conSonido ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  fontSize: '.65rem', color: 'var(--c-cyan,#00d4ff)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontWeight: 700,
                }}
              >
                {lang === 'es' ? 'Marcar todo leído' : 'Mark all read'}
              </button>
            )}
            </div>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{
                padding: '1.5rem 1rem', textAlign: 'center',
                fontSize: '.75rem', color: 'var(--c-text3,#45688a)',
              }}>
                {lang === 'es' ? 'No hay notificaciones todavía.' : 'No notifications yet.'}
              </div>
            ) : (
              items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onItemClick(it)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '.6rem .85rem',
                    border: 'none', borderBottom: '1px solid var(--c-border,#1a3550)',
                    background: it.read ? 'transparent' : 'rgba(0,212,255,.05)',
                    cursor: 'pointer',
                    color: 'var(--c-text,#c8dff0)', fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = it.read ? 'transparent' : 'rgba(0,212,255,.05)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: it.read ? 600 : 800, fontSize: '.74rem', flex: 1 }}>
                      {it.title}
                    </div>
                    {/* "3h" dice cuánto hace; el tooltip, a qué hora fue —
                        en el reloj de QUIEN MIRA. Antes la hora exacta venía
                        escrita por el servidor dentro del texto del aviso, y
                        el servidor va en UTC: una firma de las 10:35 de la
                        noche en Florida salía como "02:35 UTC" del día
                        siguiente (Lissette 2026-09-01). */}
                    <span
                      title={horaExacta(it.createdAt)}
                      style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {haceCuanto(it.createdAt, lang)}
                    </span>
                  </div>
                  {it.body && (
                    <div style={{ fontSize: '.68rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2, lineHeight: 1.4 }}>
                      {/* Los avisos guardados ANTES del arreglo llevan la hora en
                          UTC escrita dentro del texto. Se convierte al pintar: un
                          despliegue no cambia un dato ya guardado. */}
                      {cuerpoDelAviso(it.body)}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
